import { ColumnTable, Button } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'reactstrap';

import { TranslationsKeys } from '../../../../locales/translations';
import {
  CeAcademicOffer,
  CeAcademicOfferStatus,
  ContinuousEducationType,
} from '../types';

/**
 * Obtiene el primer AcademicOfferStudyPlanVersionOffer de la oferta
 */
const getFirstAospvo = (offer: CeAcademicOffer) => {
  return offer.academicOfferStudyPlanVersionOffers?.[0];
};

/**
 * Obtiene el tipo de programa (COURSE/PROGRAM) desde CeStudyPlan
 */
const getProgramType = (
  offer: CeAcademicOffer,
): ContinuousEducationType | null => {
  const aospvo = getFirstAospvo(offer);
  return (
    aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.studyPlanCe
      ?.type ?? null
  );
};

/**
 * Obtiene el texto del tipo de programa
 */
const getProgramTypeText = (
  programType: ContinuousEducationType | null,
  t: (key: string) => string,
): string => {
  if (!programType) return '-';
  const texts = {
    [ContinuousEducationType.COURSE]: t('table.programType.course'),
    [ContinuousEducationType.PROGRAM_COURSE]: t('table.programType.program'),
  };
  return texts[programType] || '-';
};

/**
 * Obtiene el tipo de proceso desde admissions -> subprocess -> process -> tuitionProcessType
 */
const getProcessTypeName = (offer: CeAcademicOffer): string | null => {
  const aospvo = getFirstAospvo(offer);
  const admissions = aospvo?.admissions;
  if (!admissions || admissions.length === 0) return null;
  return admissions[0]?.subprocess?.process?.tuitionProcessType?.name ?? null;
};

/**
 * Obtiene el total de inscritos
 */
const getEnrolledCount = (offer: CeAcademicOffer): number | string => {
  return offer.enrolled ?? 0;
};

const getBadgeColor = (status: CeAcademicOfferStatus) =>
  ({
    [CeAcademicOfferStatus.Borrador]: '#ffe6a1',
    [CeAcademicOfferStatus.Activa]: '#CEF0DA',
    [CeAcademicOfferStatus.Cerrada]: '#F2C0BD',
  }[status] ?? 'secondary');

export const useColumns = () => {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );

  const columns = useMemo<ColumnTable<CeAcademicOffer>[]>(() => {
    const getStatusBadge = (status: CeAcademicOfferStatus) => {
      const color = getBadgeColor(status);

      const labels = {
        [CeAcademicOfferStatus.Borrador]: t('table.status.draft'),
        [CeAcademicOfferStatus.Activa]: t('table.status.active'),
        [CeAcademicOfferStatus.Cerrada]: t('table.status.closed'),
      };
      return (
        <Badge
          className="status-badge fs-16 fw-400 py-2 px-4 text-dark"
          style={{ backgroundColor: color, width: '110px' }}
          pill
        >
          {labels[status] || status}
        </Badge>
      );
    };

    return [
      {
        columnName: 'processType',
        headerText: t('table.headers.processType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProcessTypeName(row) ?? '-',
      },
      {
        columnName: 'programType',
        headerText: t('table.headers.programType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProgramTypeText(getProgramType(row), t),
      },
      {
        columnName: 'name',
        headerText: t('table.headers.name'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.name ??
            '-'
          );
        },
      },
      {
        columnName: 'modality',
        headerText: t('table.headers.modality'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.modality
              ?.name ?? '-'
          );
        },
      },
      {
        columnName: 'campus',
        headerText: t('table.headers.campus'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.studyPlanVersionOffer?.campus?.name ?? '-';
        },
      },
      {
        columnName: 'startDate',
        headerText: t('table.headers.startDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          // este será un campo nuevo
          return 'inicio de clases';
        },
      },
      {
        columnName: 'endDate',
        headerText: t('table.headers.endDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          // este será un campo nuevo
          return 'fin de clases';
        },
      },
      {
        columnName: 'quota',
        headerText: t('table.headers.quota'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.quota ?? '-';
        },
      },
      {
        columnName: 'enrolled',
        headerText: t('table.headers.enrolled'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getEnrolledCount(row),
      },
      {
        columnName: 'status',
        headerText: t('table.headers.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getStatusBadge(row.status),
      },
      {
        columnName: 'actions',
        headerText: t('table.headers.actions'),
        thClassName: 'text-center',
        tdClassName: 'd-flex gap-2 justify-content-center align-items-center',
        cellFormat: ({ row }) => (
          <>
            <Button
              text={t('table.actions.delete')}
              outlined
              size="sm"
              className="mx-2"
              onClick={() => {
                alert(`Eliminar oferta con id ${row.id}`);
              }}
            />
            <Button
              text={t('table.actions.edit')}
              size="sm"
              onClick={() => alert(`Gestionar oferta con id ${row.id}`)}
            />
          </>
        ),
      },
    ];
  }, [t]);

  return columns;
};
